import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ResponseArtifactDTO } from '@dreamai/types';
import api from '../../api';
import { useResponseArtifactEvents } from '../../providers';
import ProjectAppBlockRenderer from './canvas/ProjectAppBlockRenderer';
import ShareProjectAppButton from './canvas/ShareProjectAppButton';
import { ProjectAppLoadingProvider } from '../../providers/ProjectAppViewLoadingProvider';
import TasksSkeleton from './sidebar/TasksSkeleton';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import { ReactComponent as ProjectFolderIcon } from '../../icons/project-folder.svg';
import { ReactComponent as TrashBinIcon } from '../../icons/trash-bin.svg';
import './documents-page.scss';

interface DocumentNote extends ResponseArtifactDTO {
  isShared?: boolean;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  const day = DAYS[d.getDay()];
  const date = d.getDate();
  const month = MONTHS[d.getMonth()];
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${date} ${month}, ${hours}:${mins}`;
};

const DocumentsPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();

  const [documents, setDocuments] = useState<DocumentNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Detail view state — initialize from URL param so we skip loading flash
  const [openDoc, setOpenDoc] = useState<DocumentNote | null>(() => {
    const researchAppId = searchParams.get('research_app_id');
    if (researchAppId) return { projectAppId: researchAppId } as DocumentNote;
    return null;
  });

  const fetchDocuments = useCallback(
    async (offset = 0) => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<{
          success: boolean;
          artifacts: DocumentNote[];
          hasMore: boolean;
        }>(`/response-artifacts/user/notes?limit=20&offset=${offset}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const fetched = res.data.artifacts || [];
        if (offset === 0) {
          setDocuments(fetched);
        } else {
          setDocuments((prev) => [...prev, ...fetched]);
        }
        setHasMore(res.data.hasMore);
      } catch (err) {
        console.error('Error fetching documents:', err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [getAccessTokenSilently],
  );

  useEffect(() => {
    fetchDocuments(0);
  }, [fetchDocuments]);

  // ─── Real-time updates: refetch first page and merge when artifacts change ──
  const {
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
  } = useResponseArtifactEvents();
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const silentRefresh = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        success: boolean;
        artifacts: DocumentNote[];
        hasMore: boolean;
      }>(`/response-artifacts/user/notes?limit=20&offset=0`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fetched = res.data.artifacts || [];
      setDocuments((prev) => {
        const fetchedIds = new Set(fetched.map((d) => d.id));
        const remaining = prev.filter((d) => !fetchedIds.has(d.id));
        return [...fetched, ...remaining].sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt || 0).getTime() -
            new Date(a.updatedAt || a.createdAt || 0).getTime(),
        );
      });
    } catch (err) {
      console.error('Error refreshing documents:', err);
    }
  }, [getAccessTokenSilently]);

  const scheduleSilentRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    refreshTimeoutRef.current = setTimeout(() => {
      refreshTimeoutRef.current = null;
      silentRefresh();
    }, 500);
  }, [silentRefresh]);

  useEffect(() => {
    const callbackId = 'documents-page';

    registerCreatedCallback(callbackId, (data) => {
      if (data.artifact?.artifactType === 'information_dump') {
        scheduleSilentRefresh();
      }
    });

    // Updated events don't include the artifact type, so refresh on all of them
    registerUpdatedCallback(callbackId, () => {
      scheduleSilentRefresh();
    });

    return () => {
      unregisterCreatedCallback(callbackId);
      unregisterUpdatedCallback(callbackId);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
    scheduleSilentRefresh,
  ]);

  // ─── Auto-open document from URL param (e.g. clicked in ThreadView) ──
  useEffect(() => {
    const researchAppId = searchParams.get('research_app_id');
    if (researchAppId && !openDoc) {
      const match = documents.find((d) => d.projectAppId === researchAppId);
      if (match) {
        setOpenDoc(match);
      } else {
        // Document not in loaded list — render directly with projectAppId
        setOpenDoc({ projectAppId: researchAppId } as DocumentNote);
      }
    }
  }, [searchParams, documents]);

  const handleLoadMore = useCallback(async () => {
    setLoadingMore(true);
    await fetchDocuments(documents.length);
  }, [fetchDocuments, documents.length]);

  const handleOpenDoc = useCallback(
    async (doc: DocumentNote) => {
      setOpenDoc(doc);

      // Mark as read if unread
      if (doc.unread) {
        setDocuments((prev) =>
          prev.map((d) => (d.id === doc.id ? { ...d, unread: false } : d)),
        );
        try {
          const token = await getAccessTokenSilently();
          await api.patch(
            `/response-artifacts/${doc.id}/read`,
            {},
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } catch (error) {
          console.error('Error marking document as read:', error);
        }
      }
    },
    [getAccessTokenSilently],
  );

  const handleArchive = useCallback(
    async (e: React.MouseEvent, doc: DocumentNote) => {
      e.stopPropagation();
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
      try {
        const token = await getAccessTokenSilently();
        await api.patch(
          `/response-artifacts/${doc.id}/archive`,
          { archived: true },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (error) {
        console.error('Error archiving document:', error);
        setDocuments((prev) =>
          [...prev, doc].sort(
            (a, b) =>
              new Date(b.updatedAt || b.createdAt || 0).getTime() -
              new Date(a.updatedAt || a.createdAt || 0).getTime(),
          ),
        );
      }
    },
    [getAccessTokenSilently],
  );

  const handleBack = useCallback(() => {
    setOpenDoc(null);
    // Clear research_app_id from URL when going back
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('research_app_id');
        next.delete('app_tab');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // ─── Detail View ─────────────────────────────────────────────────
  if (openDoc && openDoc.projectAppId) {
    return (
      <div className='documents-page'>
        <div className='documents-detail'>
          <div className='documents-detail-header'>
            <button className='documents-back-btn' onClick={handleBack}>
              <ArrowLeftIcon />
              <span>Back to All Documents</span>
            </button>
            <ShareProjectAppButton appId={openDoc.projectAppId} />
          </div>
          <div className='documents-detail-body'>
            <ProjectAppLoadingProvider>
              <ProjectAppBlockRenderer appId={openDoc.projectAppId} />
            </ProjectAppLoadingProvider>
          </div>
        </div>
      </div>
    );
  }

  // ─── List View ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div className='documents-page'>
        <TasksSkeleton />
      </div>
    );
  }

  return (
    <div className='documents-page'>
      <div className='documents-section'>
        <div className='documents-header'>
          <h1 className='documents-heading'>Documents</h1>
        </div>

        {documents.length === 0 ? (
          <div className='documents-empty'>
            <p>No documents yet</p>
            <small>Documents created by your assistant will appear here.</small>
          </div>
        ) : (
          <>
            <div className='documents-table'>
              <div className='documents-table-header'>
                <div className='documents-th documents-th--title'>
                  Document title
                </div>
                <div className='documents-th documents-th--updated'>
                  Last updated
                </div>
                <div className='documents-th documents-th--shared'>Shared</div>
                <div className='documents-th documents-th--actions' />
              </div>
              <div className='documents-table-body'>
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className='documents-row'
                    onClick={() => handleOpenDoc(doc)}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleOpenDoc(doc);
                      }
                    }}
                  >
                    <div className='documents-td documents-td--title'>
                      <span className='documents-title-text'>
                        {doc.title || 'Untitled Document'}
                      </span>
                      {doc.parentThreadDescription && (
                        <span className='documents-subtitle'>
                          <ProjectFolderIcon className='documents-subtitle-icon' />
                          {doc.parentThreadDescription}
                        </span>
                      )}
                    </div>
                    <div className='documents-td documents-td--updated'>
                      {doc.updatedAt
                        ? formatDate(doc.updatedAt)
                        : doc.createdAt
                          ? formatDate(doc.createdAt)
                          : '—'}
                    </div>
                    <div className='documents-td documents-td--shared'>
                      <span
                        className={`documents-shared-badge${doc.isShared ? ' shared' : ''}`}
                      >
                        {doc.isShared ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className='documents-td documents-td--actions'>
                      <button
                        className='documents-archive-btn'
                        onClick={(e) => handleArchive(e, doc)}
                        aria-label='Archive document'
                        title='Archive'
                      >
                        <TrashBinIcon />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {hasMore && (
              <button
                className='documents-load-more'
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Load more'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentsPage;
